import React, { useEffect, useMemo, useState } from "react";
import { Grid, Collapse, Divider } from "@mui/material";
import CatalogueHookProduct from "./CatalogueHookProduct";
import { ReactComponent as DownArrow } from "assets/icons/catalogViewIcons/DownArrow.svg";
import { ReactComponent as CollapseAll } from "assets/icons/catalogViewIcons/CollapseAll.svg";
import IncompleteSetModal from "components/ui/inCompleteSetModal";

const SetView = ({
  allData,
  innerChnages,
  isEdit,
  checkedItems,
  handleCheckboxChange,
  cartData,
  catalogId,
  catalogueName,
  catalogProductsPresence,
}) => {
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [setsCollapsed, setSetsCollapsed] = useState(false);
  const [noSetCollapsed, setNoSetCollapsed] = useState(false);
  const [setCollapsed, setSetCollapsed] = useState({});
  const [incompleteModal, setIncompleteModal] = useState({
    open: false,
    setId: null,
    designsInCatalogForSet: [],
  });

  // Split designs into "has sets" and "no set"
  const { setMap, noSetDesigns } = useMemo(() => {
    const setMap = new Map(); // setNumber -> { designs: [], seenIds: Set, numOfStyles }
    const noSetDesigns = [];

    const parseNumOfStyles = (raw) => {
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    };

    allData.forEach((design) => {
      const sets = design.setDetails || [];

      if (sets.length === 0) {
        noSetDesigns.push(design);
      } else {
        sets.forEach(({ setNumber, numOfStyles }) => {
          if (setNumber == null || setNumber === "") return;

          const nSafe = parseNumOfStyles(numOfStyles);

          if (!setMap.has(setNumber)) {
            setMap.set(setNumber, { designs: [], seenIds: new Set(), numOfStyles: nSafe });
          }
          const entry = setMap.get(setNumber);
          entry.numOfStyles = Math.max(entry.numOfStyles, nSafe);

          const designKey = String(design._id ?? design.id ?? design.styleNumber ?? "");
          if (!entry.seenIds.has(designKey)) {
            entry.seenIds.add(designKey);
            entry.designs.push(design);
          }
        });
      }
    });

    // Expected count can differ per row until API fills all designs — take max from every design's setDetails for this set
    setMap.forEach((entry, setNumber) => {
      entry.designs.forEach((design) => {
        (design.setDetails || []).forEach((sd) => {
          if (sd?.setNumber !== setNumber) return;
          entry.numOfStyles = Math.max(entry.numOfStyles, parseNumOfStyles(sd.numOfStyles));
        });
      });
    });

    const cleaned = new Map(
      [...setMap].map(([setNumber, { designs, numOfStyles }]) => [setNumber, { designs, numOfStyles }])
    );

    const sorted = new Map([...cleaned].sort((a, b) => a[0].localeCompare(b[0])));
    return { setMap: sorted, noSetDesigns };
  }, [allData, catalogProductsPresence]);

  const totalSetsCount = setMap.size;

  const uniqueDesignsInSetsCount = useMemo(() => {
    const ids = new Set();
    allData.forEach((design) => {
      if ((design.setDetails || []).length === 0) return;
      const key = design._id ?? design.id;
      if (key != null && key !== "") ids.add(String(key));
    });
    return ids.size;
  }, [allData, catalogProductsPresence]);

  const toggleAll = () => {
    const next = !allCollapsed;
    setAllCollapsed(next);
    setSetsCollapsed(next);
    setNoSetCollapsed(next);
    const newState = {};
    setMap.forEach((_, setNumber) => {
      newState[setNumber] = next;
    });
    setSetCollapsed(newState);
  };

  const toggleSetsSection = () => {
    const next = !setsCollapsed;
    setSetsCollapsed(next);
    const newState = {};
    setMap.forEach((_, setNumber) => {
      newState[setNumber] = next;
    });
    setSetCollapsed(newState);
  };

  const toggleSet = (setNumber) => {
    setSetCollapsed((prev) => ({
      ...prev,
      [setNumber]: !prev[setNumber],
    }));
  };

  const openIncompleteModal = (setNumber, designs) => {
    const setId = designs?.[0]?.setDetails?.find((sd) => sd.setNumber === setNumber)?._id ?? null;
    setIncompleteModal({
      open: true,
      setId,
      designsInCatalogForSet: designs,
    });
  };

  useEffect(() => {
    const setNumbers = [...setMap.keys()];
    const setsSectionFullyCollapsed =
      setNumbers.length === 0 ||
      setsCollapsed ||
      setNumbers.every((setNumber) => Boolean(setCollapsed[setNumber]));
    const noSetSectionCollapsed = noSetDesigns.length === 0 || noSetCollapsed;
    const everySectionCollapsed = setsSectionFullyCollapsed && noSetSectionCollapsed;

    if (allCollapsed !== everySectionCollapsed) {
      setAllCollapsed(everySectionCollapsed);
    }
  }, [setMap, setCollapsed, setsCollapsed, noSetDesigns.length, noSetCollapsed, allCollapsed]);

  return (
    <div style={{ width: "100%" }}>

      {/* TOP: "All Sets" with collapse-all chevron */}
      <div style={{
        fontFamily: "Poppins",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: ".9vw",
        color: "white",
        fontWeight: "400",
        height: "3.5vw",
        width: "100%",
        padding: "0 .5vw",
        boxSizing: "border-box",
        fontSize: "24px",
      }}>
        <span>All Sets</span>
        <span
          onClick={toggleAll}
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === "Enter" && toggleAll()}
        >
          <CollapseAll
            style={{
              transform: allCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          />
        </span>
      </div>

      <Divider sx={{ borderColor: "#1a1a1a", width: "100%", marginBottom: "4px" }} />

      {/* SETS SECTION HEADER: "SET - 6(26)" */}
      {totalSetsCount > 0 && (
        <div style={{ width: "100%" }}>
          <div
            style={{
              fontFamily: "Poppins",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "#FFFFFF",
              fontWeight: 400,
              fontSize: "24px",
              lineHeight: "36px",
              letterSpacing: "0px",
              minHeight: "45px",
              width: "100%",
              padding: "0 .5vw",
              boxSizing: "border-box",
              cursor: "pointer",
            }}
            onClick={toggleSetsSection}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === "Enter" && toggleSetsSection()}
          >
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontSize: "24px",
                lineHeight: "36px",
                letterSpacing: "0px",
              }}
            >
              SET - {totalSetsCount}({uniqueDesignsInSetsCount})
            </span>
            <DownArrow
              style={{
                transform: setsCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
                flexShrink: 0,
              }}
            />
          </div>

          <Collapse in={!setsCollapsed} timeout={300} unmountOnExit={false}>
            {[...setMap].map(([setNumber, { designs, numOfStyles }]) => {
              const isIncomplete = designs.length < numOfStyles;
              const isSetCollapsed = setCollapsed[setNumber];

              return (
                <div key={setNumber} style={{ width: "100%" }}>
                  {/* Row 1: collapse targets only (no shared parent onClick — avoids bubbling issues with React/MUI). */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      fontWeight: "500",
                      minHeight: "2.8vw",
                      width: "100%",
                      padding: "0 .5vw",
                      boxSizing: "border-box",
                      fontSize: "18px",
                      color: "#EDEDEB",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                        fontFamily: "Poppins",
                      }}
                    >
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleSet(setNumber);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSet(setNumber);
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {setNumber} - {designs.length}
                      </span>
                      {isIncomplete && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (typeof e.nativeEvent.stopImmediatePropagation === "function") {
                              e.nativeEvent.stopImmediatePropagation();
                            }
                            openIncompleteModal(setNumber, designs);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            if (typeof e.nativeEvent.stopImmediatePropagation === "function") {
                              e.nativeEvent.stopImmediatePropagation();
                            }
                          }}
                          style={{
                            margin: 0,
                            padding: 0,
                            border: "none",
                            background: "none",
                            color: "#FFFFFF",
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "20px",
                            letterSpacing: "0px",
                            textDecoration: "underline",
                            cursor: "pointer",
                            verticalAlign: "baseline",
                          }}
                        >
                          incomplete set
                        </button>
                      )}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSet(setNumber);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleSet(setNumber);
                        }
                      }}
                      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                    >
                      <DownArrow
                        style={{
                          transform: isSetCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                          flexShrink: 0,
                          pointerEvents: "none",
                        }}
                      />
                    </span>
                  </div>

                  {/* SET PRODUCTS */}
                  <Collapse in={!isSetCollapsed} timeout={300} unmountOnExit={false}>
                    <Grid
                      container
                      direction="row"
                      justifyContent="flex-start"
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        gap: "1.8vw",
                        rowGap: "1.8vw",
                        padding: "10px 0 20px 0",
                        width: "100%",
                      }}
                    >
                      {designs.map((design, index) => (
                        <Grid item key={`${design?.id}-${setNumber}-${index}`} className="product">
                          <CatalogueHookProduct
                            design={design}
                            index={`${index}-${design?.id}`}
                            innerChnages={innerChnages}
                            isEdit={isEdit}
                            checkedItems={checkedItems}
                            handleCheckboxChange={handleCheckboxChange}
                            cartData={cartData}
                            allData={allData}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Collapse>

                  {/* <Divider sx={{ borderColor: "#0D0D0D", border: "0.15vw solid", width: "100%", marginBottom: "4px" }} /> */}
                  <hr style={{ opacity: 1, borderWidth: "1px", borderColor: "#444444" }} />
                </div>
              );
            })}
          </Collapse>

          {/* <Divider sx={{ borderColor: "#1a1a1a", width: "100%", marginBottom: "4px" }} /> */}
          <hr style={{ opacity: 1, borderWidth: "1px", borderColor: "#444444" }} />
        </div>
      )}

      {/* NO SET SECTION */}
      {noSetDesigns.length > 0 && (
        <div style={{ width: "100%" }}>
          <div
            style={{
              fontFamily: "Poppins",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "24px",
              color: "white",
              fontWeight: "400",
              height: "3vw",
              width: "100%",
              padding: "0 .5vw",
              boxSizing: "border-box",
              cursor: "pointer",
            }}
            onClick={() => setNoSetCollapsed((prev) => !prev)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === "Enter" && setNoSetCollapsed((prev) => !prev)}
          >
            <span>NO SET - {noSetDesigns.length}</span>
            <DownArrow
              style={{
                transform: noSetCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
                flexShrink: 0,
              }}
            />
          </div>

          <Collapse in={!noSetCollapsed} timeout={300} unmountOnExit={false}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: "1.8vw",
                rowGap: "1.8vw",
                padding: "10px 0 20px 0",
                width: "100%",
              }}
            >
              {noSetDesigns.map((design, index) => (
                <Grid item key={`noset-${design?.id}-${index}`} className="product">
                  <CatalogueHookProduct
                    design={design}
                    index={`${index}-${design?.id}`}
                    innerChnages={innerChnages}
                    isEdit={isEdit}
                    checkedItems={checkedItems}
                    handleCheckboxChange={handleCheckboxChange}
                    cartData={cartData}
                    allData={allData}
                  />
                </Grid>
              ))}
            </Grid>
          </Collapse>

          {/* <Divider sx={{ borderColor: "#0D0D0D", border: "0.15vw solid", width: "100%", marginBottom: "4px" }} /> */}
          <hr style={{ opacity: 1, borderWidth: "1px", borderColor: "#444444" }} />
        </div>
      )}

      <IncompleteSetModal
        open={incompleteModal.open}
        onClose={() => setIncompleteModal((s) => ({ ...s, open: false }))}
        setId={incompleteModal.setId}
        designsInCatalogForSet={incompleteModal.designsInCatalogForSet}
        catalogId={catalogId}
        catalogueName={catalogueName}
        catalogProductsPresence={catalogProductsPresence}
      />
    </div>
  );
};

export default SetView;